import React, { useEffect } from 'react';
import { Box, Button, Divider, Grid, Paper, Typography, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { find, map } from 'lodash';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomTextInput from '../CustomTextInput';
import RegistrationTypeSelect from './RegistrationTypeSelect';
import StateSelect from './StateSelect';
import CountrySelect from './CountrySelect';
import TopBar from '../TopBar';
import { B2CCustomerValidation } from '../../validations/CustomerValidation';

export default function B2CCustomerForm({ onSubmit, onCancel, initialData = {}, apiErrors = {} }) {
  const theme = useTheme();
  const { countryCodes } = useSelector((state) => state.countryCodes);
  const { states } = useSelector((state) => state.states);
  const { msicCodes } = useSelector((state) => state.msicCodes);

  let country = find(countryCodes, { Code: initialData.country_code });
  let state = find(states, { Code: initialData.state });
  let msicCode = find(msicCodes, { Code: initialData.msic_code });

  const {
    register,
    control,
    getValues,
    setValue,
    watch,
    handleSubmit,
    setError,
    formState: { errors },
    trigger,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(B2CCustomerValidation),
    defaultValues: {
      ...initialData,
      country_code: country ? { key: country.Code, value: country.Country } : null,
      state: country === 'MYS' ? (state ? { key: state.Code, value: state.State } : '') : state || '',
      registration_type: initialData.registration_type
        ? {
            key: initialData.registration_type,
            value: initialData.registration_type,
          }
        : null,
    },
  });

  useEffect(() => {
    map(apiErrors, (value, key) => {
      setError(key, { message: value, type: 'string' });
    });
  }, [setError, apiErrors]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  const selectedCountry = watch('country_code');
  useEffect(() => {
    if (selectedCountry?.key !== 'MYS') {
      if (typeof getValues('state') === 'object') {
        setValue('state', '');
      }
    } else {
      if (typeof getValues('state') === 'string') {
        setValue('state', '');
      }
    }
  }, [selectedCountry, setValue, getValues]);
  return (
    <Paper elevation={0} sx={{ p: '1rem' }}>
      {
        <Box sx={{ pb: 4 }}>
          <Grid
            container
            sx={{
              display: 'block',
            }}
          >
            <Grid xs={12}>
              <TopBar title={'Enter your details to request an E-Invoice'} />
            </Grid>
          </Grid>
        </Box>
      }
      <Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput name="name" control={control} errors={errors} label={'Name'} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput name="email" control={control} errors={errors} label={'Email'} />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
          Address
          <Divider></Divider>
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput name="address1" control={control} errors={errors} label={'Address Line 1'} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput name="address2" control={control} errors={errors} label={'Address Line 2'} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput name="address3" control={control} errors={errors} label={'Address Line 3'} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput name="city" control={control} errors={errors} label={'City'} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountrySelect name="country_code" control={control} errors={errors} label={'Country'} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            {selectedCountry?.key === 'MYS' ? (
              <StateSelect errors={errors} control={control} name="state" label={'State'} />
            ) : (
              <CustomTextInput name="state" control={control} errors={errors} label={'State'} />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput name="postal_code" control={control} errors={errors} label={'Postal Code'} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput name="phone" control={control} errors={errors} label={'Phone'} />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
          ID
          <Divider></Divider>
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RegistrationTypeSelect
              errors={errors}
              control={control}
              name="registration_type"
              label={'ID Type'}
              options={[
                {
                  key: 'PASSPORT',
                  value: 'PASSPORT',
                },
                {
                  key: 'ARMY',
                  value: 'ARMY',
                },
                {
                  key: 'NRIC',
                  value: 'NRIC',
                },
              ]}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput name="registration_number" control={control} errors={errors} label={'ID Number'} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput name="tin" control={control} errors={errors} label={'TIN'} />
          </Grid>
        </Grid>

        <Grid xs={12} mt={4}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(1) }}>
            <Button key={'cancel'} variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button key={'save'} variant="contained" onClick={handleSubmit(onSubmit)}>
              Request E-Invoice
            </Button>
          </Box>
        </Grid>
      </Box>
    </Paper>
  );
}
